/**
 * The write half of the inquiry contract, plus the three reads B4 needs that the
 * shared client does not carry.
 *
 * ===========================================================================
 * WHY THIS IS NOT IN `src/lib/inquiries-api.ts`
 * ===========================================================================
 * That module is shared with the dashboard (a different work stream owns it) and
 * carries exactly what BOTH features need: the list, stage-counts, the DTO shapes
 * and the query keys. Mutations, party search and the per-record event feed are
 * consumed only here, so they live here — one owner per file, and the shared module
 * keeps a surface small enough that two streams cannot break each other.
 *
 * Nothing is re-declared: every DTO type is imported from the shared module, and
 * `inquiryKeys` is re-exported rather than copied so both features invalidate the
 * SAME cache entries. A second copy of `inquiryKeys.list` would mean a stage change
 * on the board silently not refreshing the dashboard's widgets.
 *
 * Every call goes through `apiRequest`, which owns the single-flight refresh mutex
 * (D-web-1). ESLint forbids `fetch` outside `src/lib/api.ts` precisely so a helper
 * like this cannot quietly open a second refresh path and trip revoke-on-rotate.
 */

import { apiRequest } from '../../lib/api';
import { inquiryKeys } from '../../lib/inquiries-api';
import type {
  FollowupDto,
  FollowupKind,
  InquiryRecordDto,
  Paged,
  TallyEdition,
} from '../../lib/inquiries-api';
import type { EventRow } from '../../domain/events';
import type { InquiryStage } from '../../domain/stages';
// TYPE-ONLY, and that is the whole rule. `@crf/shared` compiles to CommonJS
// (`packages/shared/tsconfig.json`), so importing a VALUE out of it — and `WarningCode` is
// a TS enum, i.e. a runtime object — would drag a CJS module into the browser bundle. See
// the header of `src/lib/permissions.ts`, which gives `ErrorCode` exactly this treatment.
import type { WarningCode } from '@crf/shared';

export { inquiryKeys };

/* ─────────────────────────────────────────────────────────────────── parties ── */

/**
 * `parties` as the pipeline needs it — transcribed from `PartyDto`
 * (`apps/api/src/modules/parties/parties.service.ts:53`).
 *
 * The §9.2/D4 discriminators (`gst_state_code`, `is_sez`, `is_group_entity`,
 * `group_entity_country_id`) are in the API shape and omitted here: no B4 screen
 * renders them, and a field this module claims to know but never checks is a field
 * that will be wrong the first time somebody trusts it.
 */
export interface PartyDto {
  id: string;
  country_id: number;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  tax_no: string | null;
  /**
   * §B3 — the customer's OWN country, for a scope that sells outside its borders.
   *
   * NULL for the five domestic scopes: there the customer is in the selling country by
   * definition, and repeating the code would be noise on every row. Non-null for XM
   * (GS Multi-trade), where it is the only field that separates a Kenyan customer from
   * an Omani one — so every surface that identifies a party has to show it when present.
   *
   * OPTIONAL ON THE TYPE, because this mirror is also satisfied by fixtures and by the
   * inquiry screens that predate the column; a missing key and an explicit null both mean
   * "not stated", and the renderers treat them the same.
   */
  customer_country?: string | null;
  phone: string | null;
  email: string | null;
  /**
   * WHO SENT US THIS CUSTOMER — `parties.referral` (migration 49; owner ruling R4
   * *"capture them, never require them"* + the v11.0 amendment: *"Referral … belongs on
   * the party, not only on the document"*). The legacy party master's `RefBy`. Free text
   * or NULL — a person, a firm, a partner, or the word `Direct`; never an FK, never a
   * vocabulary (the migration's docblock says why: an FK cannot hold `Direct`, and a
   * nullable FK collapses "the sale was direct" into "nobody told me").
   *
   * ON EVERY PARTY READ, because the API's `toPartyDto` is the ONE party mapper (list,
   * record, create echo, link echo — measured there by grep). It is here so the CRF
   * create page can PREFILL its "Referred by" from the customer the coordinator just
   * picked (`CrfCreatePage`); the server-side inherit in `CrfsService.create` covers the
   * clients that do not prefill.
   *
   * OPTIONAL ON THE TYPE for the same reason `customer_country` is: this mirror is also
   * satisfied by fixtures and by screens that predate the column, and a missing key and
   * an explicit null both mean "nobody said" — the renderers treat them the same.
   *
   * NOT MONEY. It never arrives masked; a Developer whose `amount` is `•••` still reads
   * the referrer's name (`test/referral-capture.e2e-spec.ts` proves both on one response).
   */
  referral?: string | null;
  source: 'tally' | 'prospect';
  /**
   * Migration 63, v24 A1 — where a legacy-era row came from. NULL/absent = native;
   * 'imported' = carried from the legacy partymaster; 'minted_unmatched' = a prospect
   * the importer minted so no history was skipped (R10-A). The pickers' server-side
   * default-exclusion keys on 'minted_unmatched'; a row that DOES arrive says so on
   * its option line. OPTIONAL for the same fixture/mirror reason as `referral` above.
   */
  legacy_origin?: 'imported' | 'minted_unmatched' | null;
  /**
   * PB-6 — both twins of a name-only residual duplicate group carry this; the party
   * record renders it as ONE QUIET CHIP and nothing else (no write surface anywhere —
   * the API wall is legacy-filter.e2e-spec.ts case c2). OPTIONAL as above.
   */
  possible_duplicate?: boolean;
  tally_guid: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreatePartyBody {
  name: string;
  city?: string | null;
  state?: string | null;
  phone?: string | null;
  email?: string | null;
  tax_no?: string | null;
  address?: string | null;
  /**
   * §B3 — REQUIRED by the API for an international scope, absent for the other five.
   *
   * `POST /countries/:cc/parties` refuses a create for `XM` that does not carry it
   * (`parties.service.ts` `resolveCustomerCountry`, `INTERNATIONAL_SCOPES = ['XM']`) —
   * a refusal, not a default, because XM is not a place and falling back to the
   * scope's own country would record every international customer as sitting in a
   * UAE Free Zone.
   *
   * ⚠ This key was MISSING here, and that is what made the browser's create surface
   * unable to satisfy the rule: the field was not merely unsent, it was
   * UNEXPRESSIBLE — a call site adding it was a TypeScript excess-property error —
   * so every XM prospect created from the browser was a guaranteed 422 with no field
   * on screen that could fix it. The server DTO always accepted it; the wire contract
   * always had room.
   *
   * `string`, NOT `string | null`, unlike its neighbours: this is an omit-when-blank
   * field. An explicit null on a domestic create would claim the surface had asked
   * the question and been told "nowhere", which is a different fact from not asking.
   */
  customer_country?: string;
}

/**
 * Party typeahead for the New Inquiry panel.
 *
 * `perPage` is capped at 8 because this is a picker, not a list: the panel shows a
 * short menu and the operator refines the term. Asking for 50 rows to render 8 is
 * bandwidth spent on a scan the API answers with `LIKE '%term%'`.
 */
export async function searchParties(
  countryCode: string,
  q: string,
  includeHistory = false,
  signal?: AbortSignal,
): Promise<Paged<PartyDto>> {
  /*
   * AM-1 (v24 A1.4) — THIS IS A PICKER'S REQUEST, AND IT SAYS SO. The route's
   * tri-state reads a bare `q` as a search surface that reaches everything; a
   * picker's ruled default is to EXCLUDE `minted_unmatched` prospects behind a
   * visible toggle. So the toggle state is always sent — '0' is the visible OFF,
   * never an omitted parameter that would quietly widen every picker into a
   * history search.
   */
  return apiRequest<Paged<PartyDto>>(`/countries/${countryCode}/parties`, {
    query: { q, include_history: includeHistory ? '1' : '0', page: 1, perPage: 8 },
    signal,
  });
}

export async function createParty(
  countryCode: string,
  body: CreatePartyBody,
): Promise<PartyDto> {
  return apiRequest<PartyDto>(`/countries/${countryCode}/parties`, { method: 'POST', body });
}

/**
 * One party by id — the CUSTOMER group on the Peek and the Record page (Contact,
 * Phone, Email in `Record/PropertiesPanel`, node I12:6449;8:43).
 *
 * REQUIRES `parties.view`, which is a DIFFERENT permission from `inquiries.view`
 * (spec Appendix A). A user holding only the latter gets 403 here, so every caller
 * must gate the query on the permission and degrade to `party_name` — which the
 * inquiry row already carries — rather than showing an error where a phone number
 * should be.
 */
export async function fetchParty(id: string, signal?: AbortSignal): Promise<PartyDto> {
  return apiRequest<PartyDto>(`/parties/${id}`, { signal });
}

export const partyKeys = {
  search: (cc: string, q: string) => ['parties', cc, 'search', q] as const,
  record: (id: string) => ['party', id] as const,
};

/* ───────────────────────────────────────────────────────────────── inquiries ── */

/**
 * `POST /countries/:cc/inquiries`.
 *
 * `party_id` is the only required field (`CreateInquiryDto`). `doc_no`,
 * `stage`, `stage_entered_at`, `country_id` and `created_by` are all server-side and
 * are deliberately absent — the global `ValidationPipe` runs with
 * `forbidNonWhitelisted`, so sending one is a 400, not a silent strip.
 *
 * ⚠ `expected_value` and `currency` ARE GONE from this body, and the same
 * `forbidNonWhitelisted` sentence is why it matters: `CreateInquiryDto` no longer declares
 * them (migration 44, owner ruling Q6), so a client that kept sending either would get a
 * 400 naming it — which is the answer a stale caller should get, not a silent strip.
 */
export interface CreateInquiryBody {
  party_id: string;
  subject?: string | null;
  details?: string | null;
  assigned_to?: string | null;
  legacy_ref?: string | null;
  /**
   * The Tally details the customer gave on the call — migration 45, owner ruling Q9.
   *
   * ALL THREE OPTIONAL, and OMITTED rather than sent empty when nothing was said: the API
   * runs `forbidNonWhitelisted`, and a `tally_serial: ''` would record that the question
   * was asked and answered with nothing, which is a different fact from not asking.
   */
  tally_serial?: string | null;
  tally_edition?: TallyEdition | null;
  tally_release?: string | null;
  /**
   * The shared-vocabulary values to attach on create — `InquiryPlatformEntryDto[]` on the
   * API (migration 48, owner ruling 5). Ids come from `fetchInquiryPlatformOptions`, never
   * from a list this client holds; `detail` is the per-attachment scope text.
   *
   * OMITTED WHEN NOTHING IS TICKED, the same rule the Tally claim above follows: on a
   * create, an absent key and `[]` both mean "no values" to the server (`undefined` =
   * untouched, `[]` = cleared, and there is nothing to clear on a new row), and this page's
   * convention is to send only what was given.
   */
  platforms?: InquiryPlatformEntry[];
}

/**
 * One entry of `CreateInquiryBody.platforms` — the API's `InquiryPlatformEntryDto`,
 * transcribed: the vocabulary id and an optional per-attachment detail. NO name on the
 * wire — the server knows the words.
 */
export interface InquiryPlatformEntry {
  platform_id: string;
  detail?: string | null;
}

/**
 * One offerable value for the create screen's REQUIREMENTS picker — the API's
 * `InquiryPlatformOptionView`, mirrored, and field for field the CRF picker's
 * `PlatformOptionDto` (`features/crfs/api.ts`).
 *
 * ⚠ `applies_to` IS A LIST (migration 48) AND NOTHING ON THIS SCREEN BRANCHES ON IT. The
 * server has already excluded every value an inquiry may not carry; the field describes
 * the row, it does not instruct the client.
 */
export interface InquiryPlatformOptionDto {
  id: string;
  name: string;
  sort_order: number;
  applies_to: string[];
}

/**
 * The inquiry picker's options — what THIS country may newly attach to an inquiry today
 * (`GET /countries/:cc/inquiries/platforms`, on `inquiries.view`).
 *
 * ⚠ THE SERVER DOES THE FILTERING, and this function's whole existence is the Q8 ruling:
 * retired values, values this country has switched off, and values whose `applies_to`
 * does not carry `inquiry` are already absent from the response. The client renders what
 * it is given and holds no vocabulary of its own — if a future reader finds themselves
 * adding `const REQUIREMENTS = [...]` to avoid this fetch, that is the mirror the owner
 * named ("two lists that must stay in step is a mirror, and mirrors drift") coming back.
 *
 * This is the AFFORDANCE, not the guard: the server refuses a bad `platform_id` on write
 * regardless of what was offered here.
 */
export async function fetchInquiryPlatformOptions(
  countryCode: string,
  signal?: AbortSignal,
): Promise<InquiryPlatformOptionDto[]> {
  return apiRequest<InquiryPlatformOptionDto[]>(`/countries/${countryCode}/inquiries/platforms`, {
    signal,
  });
}

export async function createInquiry(
  countryCode: string,
  body: CreateInquiryBody,
): Promise<InquiryRecordDto> {
  return apiRequest<InquiryRecordDto>(`/countries/${countryCode}/inquiries`, {
    method: 'POST',
    body,
  });
}

export async function fetchInquiry(id: string, signal?: AbortSignal): Promise<InquiryRecordDto> {
  return apiRequest<InquiryRecordDto>(`/inquiries/${id}`, { signal });
}

/**
 * `PATCH /inquiries/:id` — the record's field edit, on `inquiries.edit`
 * (`inquiries.controller.ts:77`), which is the SAME permission the record page's other
 * edit (Add Follow-up) already runs on. Distinct from `changeInquiryStage` below: stage
 * moves have their own route, their own permission and their own rules, and this body can
 * carry neither `stage` nor `lost_reason` (the API's closed `UpdateInquiryDto`).
 *
 * DELIBERATELY NARROWER THAN THE SERVER'S DTO. `UpdateInquiryDto` also admits `subject`,
 * `details`, `assigned_to`, `legacy_ref` and the three Tally-claim fields; this type
 * declares ONLY what a screen actually sends today — the requirements editor on the
 * record page (unit 3.2). A key this module claims to know but never sends is a field
 * that will be wrong the first time somebody trusts it (the `PartyDto` rule above).
 * Widen it WITH the screen that sends the new key, not ahead of one.
 *
 * ⚠ THE WIRE DISTINCTION IS THE CONTRACT (D18, held by the D19 cross-boundary spec):
 * `platforms` ABSENT = untouched; `platforms: []` = clear every attachment. A caller
 * whose requirements are untouched must therefore not call this with the key at all —
 * the editor sends nothing when nothing changed (`RequirementsGroup` computes that), so
 * an identical set is never re-written and the attach rows keep their `created_by`/
 * `created_at` audit facts.
 */
export interface UpdateInquiryBody {
  platforms?: InquiryPlatformEntry[];
}

export async function updateInquiry(
  id: string,
  body: UpdateInquiryBody,
): Promise<InquiryRecordDto> {
  return apiRequest<InquiryRecordDto>(`/inquiries/${id}`, { method: 'PATCH', body });
}

/**
 * `PATCH /inquiries/:id/stage` — the ONLY way a stage moves.
 *
 * `reason` is mandatory for `lost` and the API answers 422 `LOST_REASON_REQUIRED`
 * when it is missing. That code is branchable on purpose (the service raises it
 * rather than a generic `VALIDATION_FAILED`), which is what lets the board reopen
 * its reason prompt instead of showing a dead-end error.
 */
export async function changeInquiryStage(
  id: string,
  body: { stage: InquiryStage; reason?: string | null },
): Promise<InquiryRecordDto> {
  return apiRequest<InquiryRecordDto>(`/inquiries/${id}/stage`, { method: 'PATCH', body });
}

export interface CreateFollowupBody {
  kind: FollowupKind;
  note: string;
  next_action_at?: string | null;
}

export async function createFollowup(
  id: string,
  body: CreateFollowupBody,
): Promise<FollowupDto> {
  return apiRequest<FollowupDto>(`/inquiries/${id}/followups`, { method: 'POST', body });
}

/* ──────────────────────────────────────────────────────────── user directory ── */

/*
 * THE USER DIRECTORY MOVED OUT — `features/directory/api.ts`.
 *
 * `DirectoryUserDto`, `DIRECTORY_PAGE`, `MIN_COLLEAGUE_SEARCH`, `fetchColleagues`
 * and `userKeys` lived here because the bulk Assign menu was the first surface to
 * need them. The work-order record surface needs the same picker, and a second
 * copy of a search cap is how the "showing N of M" honesty rots in one place and
 * not the other. Nothing about `GET /users` was ever inquiry-shaped.
 */

/* ────────────────────────────────────────────────────────────────────── bulk ── */

export type BulkAction = 'assign' | 'stage';

/** `{id, code}` — `COUNTRY_SCOPE_VIOLATION` or `NOT_FOUND` (`BulkRefusal`). */
export interface BulkRefusal {
  id: string;
  code: string;
}

/**
 * The codes the BULK route can actually emit.
 *
 * ⚠ THIS WAS `` `${WarningCode}` `` — THE WHOLE ENUM — AND IT IS NARROWED WITH A CALLOUT
 * (custom-roles design §1.7). That was true while bulk inquiry actions were the only
 * surface producing advisories. The custom-roles build adds a SECOND advisory surface —
 * role-composition guardrails on `POST/PATCH /roles` — so the shared enum now carries
 * members this route can never send, and `Record<BulkWarningCode, string>` below was
 * demanding a sentence for `ROLE_ADMINISTRATIVE` on the inquiries bulk bar.
 *
 * Writing those sentences would have been the wrong repair: it would put role copy in a
 * component that renders inquiry results, and it would make the exhaustiveness contract
 * claim something false — that this screen handles every warning in the system.
 *
 * The contract KEPT is the one that was worth having: this list is the bulk vocabulary,
 * the `Record` over it is still exhaustive, and a member added to the BULK surface still
 * breaks the build until somebody writes its sentence. `satisfies` ties every entry to
 * the shared enum, so a typo still does not compile, and `assign-colleague.test.tsx`
 * asserts the partition — bulk codes render a sentence, every other member of the shared
 * enum falls through to the echo — so the two halves cannot drift.
 */
export const BULK_WARNING_CODES = [
  'ASSIGNEE_OUT_OF_SCOPE',
] as const satisfies readonly `${WarningCode}`[];

export type BulkWarningCode = (typeof BULK_WARNING_CODES)[number];

/**
 * `{id, code}` — the SAME SHAPE as {@link BulkRefusal}, with a vocabulary that shares
 * nothing with it (owner ruling 3, `packages/shared/src/permissions.ts`).
 *
 * That split is the reason this client can render both arrays with one list renderer and
 * still never mistake a 200 advisory for a refusal: a warning is a fact about a write that
 * SUCCEEDED. `refusalReason()` and `warningReason()` are therefore separate maps, and
 * neither falls back to the other.
 *
 * `code` is typed as the union because the enum is closed today and a typo must not
 * compile. A future server that adds a member this build has never heard of is still
 * handled at RUNTIME: `warningReason()` widens to `string` and echoes an unknown code
 * verbatim rather than swallowing it.
 */
export interface BulkWarning {
  id: string;
  code: BulkWarningCode;
}

/**
 * `{updated, refused, warnings?}`.
 *
 * `updated` is a statement about the END STATE, not about writes: a row that already
 * held the requested value is a no-op and appears here without having been written.
 * The UI must not therefore say "N changed" — see `BulkResultNotice`.
 */
export interface BulkResultDto {
  updated: string[];
  refused: BulkRefusal[];
  /**
   * Per-row advisories about a write that SUCCEEDED — today, one entry per id in `updated`
   * that now holds an assignee with no role in the row's country.
   *
   * OMITTED ENTIRELY when there is nothing to warn about (owner ruling 3 / C2; the service
   * returns `{updated, refused}` verbatim on that branch). Absent means "nothing to
   * confirm", so every read site is `warnings ?? []` — treating the key as always-present
   * would put `undefined.length` on the screen the moment a batch went cleanly.
   */
  warnings?: BulkWarning[];
}

export async function bulkUpdateInquiries(
  countryCode: string,
  body: { ids: string[]; action: BulkAction; value?: string | null },
): Promise<BulkResultDto> {
  return apiRequest<BulkResultDto>(`/countries/${countryCode}/inquiries/bulk`, {
    method: 'POST',
    body,
  });
}

/** `MAX_BULK_IDS` in `dto/inquiries.dto.ts`. Over this the API answers 400. */
export const MAX_BULK_IDS = 200;

/* ──────────────────────────────────────────────────────────────────── events ── */

/**
 * The audit trail for ONE inquiry — `GET /countries/:cc/events?entity=inquiry&entityId=…`.
 *
 * Country scope is the only gate on this route (no extra permission), matching the
 * other `/countries/:cc/…` reads. It is what makes the Undo claim checkable: the
 * reverse PATCH emits its own `inquiry.stage_changed`, so this feed shows BOTH moves
 * and the trail is honest rather than rewritten.
 */
export async function fetchInquiryEvents(
  countryCode: string,
  inquiryId: string,
  signal?: AbortSignal,
): Promise<Paged<EventRow>> {
  return apiRequest<Paged<EventRow>>(`/countries/${countryCode}/events`, {
    query: { entity: 'inquiry', entityId: inquiryId, page: 1, perPage: 25 },
    signal,
  });
}

export const inquiryEventKeys = {
  forInquiry: (cc: string, id: string) => ['events', cc, 'inquiry', id] as const,
};
